import { getDataSourcesPackageName } from "@/wab/shared/codegen/react-p/data-sources";
import {
  getExportedComponentName,
  makeDefaultExternalPropsName,
  makePlasmicComponentName,
  makeTaggedPlasmicImport,
} from "@/wab/shared/codegen/react-p/serialize-utils";
import { SerializerBaseContext } from "@/wab/shared/codegen/react-p/types";
import { getReactWebPackageName } from "@/wab/shared/codegen/react-p/utils";
import { ExportOpts } from "@/wab/shared/codegen/types";
import { Component } from "@/wab/shared/model/classes";

export const MK_PATH_FROM_ROUTE_AND_PARAMS_SER = `
function mkPathFromRouteAndParams(route: string, params: ParamsRecord) {
  if (!params) {
    return route;
  }
  let path = route;
  for (const [key, value] of Object.entries(params)) {
    if (typeof value === "string") {
      path = path.replace(\`[\${key}]\`, value);
    } else if (Array.isArray(value)) {
      if (path.includes(\`[[...\${key}]]\`)) {
        path = path.replace(\`[[...\${key}]]\`, value.join("/"));
      } else if (path.includes(\`[...\${key}]\`)) {
        path = path.replace(\`[...\${key}]\`, value.join("/"));
      }
    }
  }
  return path;
}`;

export function makePlasmicServerRscComponentName(component: Component) {
  return `Plasmic${getExportedComponentName(component)}Server`;
}

export function makePlasmicClientRscComponentName(component: Component) {
  return `Client${getExportedComponentName(component)}`;
}

export function makeLoaderServerFunctionFileName(component: Component) {
  return `__loader_rsc_${getExportedComponentName(component)}_${
    component.uuid
  }.tsx`;
}

export function makePlasmicServerRscComponentFileName(component: Component) {
  return `${makePlasmicServerRscComponentName(component)}.tsx`;
}

export function makePlasmicClientRscComponentFileName(component: Component) {
  return `${makePlasmicClientRscComponentName(component)}.tsx`;
}

export function makePlasmicQueryImports(ctx: SerializerBaseContext) {
  if (ctx.useRSC || !ctx.hasServerQueries) {
    return "";
  }
  return `import { useMutablePlasmicQueryData } from "@plasmicapp/query";`;
}

export function makeDataSourcesQueryTypeImports() {
  return `import type { PlasmicQuery, PlasmicQueryResult } from "${getDataSourcesPackageName()}";`;
}

export function makeServerQueryTreeTypeImport() {
  return `import type { QueryComponentNode } from "${getDataSourcesPackageName()}";`;
}

export function makeDataSourcesServerQueryImports() {
  return `import { executePlasmicQueries } from "${getDataSourcesPackageName()}";`;
}

export function makeComponentTypeImport(
  exportOpts: ExportOpts,
  component: Component,
  opts?: {
    includePlasmicComponent?: boolean;
  },
) {
  const plasmicComponentName = makePlasmicComponentName(component);
  const genPropsName = makeDefaultExternalPropsName(component);

  const imports = [
    ...(opts?.includePlasmicComponent ? [plasmicComponentName] : []),
    genPropsName,
  ];

  const path = [
    exportOpts.relPathFromImplToManagedDir,
    makePlasmicComponentName(component),
  ].join("/");

  return makeTaggedPlasmicImport(imports, path, component.uuid, "render");
}

export function makePageParamsTypeImport(exportOpts: ExportOpts) {
  return `import type { ParamsRecord, PlasmicPageProps } from "${getReactWebPackageName(
    exportOpts,
  )}";`;
}

export function serializeMakeAppRouterPageCtx(
  ctx: SerializerBaseContext,
  opts?: { usesSearchParams?: boolean },
) {
  const pageMeta = ctx.component.pageMeta;
  if (!pageMeta) {
    return makePageParamsTypeImport(ctx.exportOpts);
  }
  // Only await searchParams if $ctx.query is used so the page can be statically generated.
  const queryExpr = opts?.usesSearchParams
    ? "(await searchParams) ?? {}"
    : "{}";
  return `${makePageParamsTypeImport(ctx.exportOpts)}
${MK_PATH_FROM_ROUTE_AND_PARAMS_SER}

export async function makeAppRouterPageCtx({ params, searchParams }: PlasmicPageProps) {
  const pageRoute = "${pageMeta.path}";
  const pageParams = (await params) ?? {};
  const pagePath = mkPathFromRouteAndParams(pageRoute, pageParams);

  const ctx = {
    pageRoute,
    pagePath,
    params: pageParams,
    query: ${queryExpr},
  };
  return ctx;
}`;
}
